"use client";

import { AiAccessCheckerForm } from "@marketing/ai-access-checker";
import { FAQSection } from "@marketing/shared/components/FAQSection";
import {
	BentoGrid,
	CtaPair,
	Section,
	SectionHeader,
} from "@marketing/shared/components/bento/Bento";
import { CollapsedTakeaways } from "@marketing/shared/components/bento/KeyTakeaways";
import { Reveal } from "@marketing/shared/components/bento/motion";
import {
	BarChart3Icon,
	BotIcon,
	LinkIcon,
	SearchIcon,
	ShieldCheckIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

const STEPS = [
	{ icon: SearchIcon, key: "step1" },
	{ icon: BotIcon, key: "step2" },
	{ icon: BarChart3Icon, key: "step3" },
	{ icon: ShieldCheckIcon, key: "step4" },
] as const;

const WHY = [
	{ icon: LinkIcon, key: "backlinks" },
	{ icon: BotIcon, key: "visibility" },
] as const;

export function AiAccessCheckerClient() {
	const t = useTranslations();

	const faqItems = Array.from({ length: 8 }, (_, i) => ({
		question: t(`marketing.aiAccessChecker.faq.q${i + 1}.question` as any),
		answer: t(`marketing.aiAccessChecker.faq.q${i + 1}.answer` as any),
	}));

	return (
		<>
			{/* Hero */}
			<Section className="pt-24 lg:pt-28">
				<div className="mx-auto max-w-3xl text-center">
					<div className="mb-6 inline-flex items-center gap-2 rounded-full border bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
						<SearchIcon className="size-4" />
						<span>{t("marketing.aiAccessChecker.badge")}</span>
					</div>
					<h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
						{t("marketing.aiAccessChecker.title")}
					</h1>
					<p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
						{t("marketing.aiAccessChecker.subtitle")}
					</p>
					<div className="mx-auto mt-10 max-w-xl">
						<AiAccessCheckerForm />
					</div>
				</div>
			</Section>

			{/* Entity-dense primary descriptive section + key takeaways (AEO) */}
			<Section muted>
				<Reveal>
					<div className="mx-auto max-w-3xl">
						<h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
							How AI crawler access shapes your visibility
						</h2>
						<div className="mt-4 space-y-3 text-lg text-muted-foreground">
							<p>
								Assistants like ChatGPT, Claude, Perplexity, and
								Google&apos;s AI Overviews can only cite pages
								their crawlers are allowed to fetch. This tool
								reads a domain&apos;s robots.txt and checks it
								against the agents that matter for AI discovery
								— OpenAI&apos;s GPTBot and OAI-SearchBot,
								Anthropic&apos;s ClaudeBot, PerplexityBot,
								Google-Extended, Amazonbot, and Bytespider — so
								you can see, agent by agent, whether the page a
								link points to is reachable or blocked.
							</p>
							<p>
								Blocking a training crawler (GPTBot,
								Google-Extended, ClaudeBot) is a content-rights
								decision; blocking a retrieval crawler
								(ChatGPT-User, Claude-User) directly removes the
								page from the sources an assistant can quote. A
								backlink or PR placement only compounds your AI
								visibility if the destination is actually
								crawlable, which is why access is the first
								thing to verify.
							</p>
						</div>
						<CollapsedTakeaways
							className="mt-6"
							summary="Key takeaways"
							facts={[
								"Retrieval crawlers (ChatGPT-User, Claude-User) fetch pages at answer time; blocking them removes you from citable sources.",
								"Training crawlers (GPTBot, Google-Extended, ClaudeBot) shape future models; blocking them is a rights decision, not a visibility one.",
								"robots.txt is the control surface — a single Disallow can make an otherwise strong page invisible to AI.",
								"Pair access with an AI Placement Value Score to see whether a reachable domain is also worth earning a link from.",
							]}
						/>
					</div>
				</Reveal>
			</Section>

			{/* How It Works */}
			<Section>
				<Reveal>
					<SectionHeader
						title={t("marketing.aiAccessChecker.howItWorks.title")}
						lede={t(
							"marketing.aiAccessChecker.howItWorks.subtitle",
						)}
					/>
				</Reveal>
				<BentoGrid className="mt-10 lg:grid-cols-4">
					{STEPS.map((step, i) => (
						<Reveal key={step.key} delay={i * 80}>
							<div className="h-full rounded-2xl border bg-card p-5 text-center">
								<div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10">
									<step.icon
										className="size-6 text-primary"
										aria-hidden
									/>
								</div>
								<h3 className="mt-4 text-base font-bold">
									{t(
										`marketing.aiAccessChecker.howItWorks.${step.key}.title` as any,
									)}
								</h3>
								<p className="mt-1.5 text-sm text-muted-foreground">
									{t(
										`marketing.aiAccessChecker.howItWorks.${step.key}.description` as any,
									)}
								</p>
							</div>
						</Reveal>
					))}
				</BentoGrid>
			</Section>

			{/* Why It Matters */}
			<Section muted>
				<Reveal>
					<SectionHeader
						title={t(
							"marketing.aiAccessChecker.whyItMatters.title",
						)}
						lede={t(
							"marketing.aiAccessChecker.whyItMatters.subtitle",
						)}
					/>
				</Reveal>
				<BentoGrid className="mt-10 lg:grid-cols-2">
					{WHY.map((card, i) => (
						<Reveal key={card.key} delay={i * 80}>
							<div className="h-full rounded-2xl border bg-card p-6">
								<div className="mb-4 flex items-center gap-3">
									<card.icon
										className="size-6 text-primary"
										aria-hidden
									/>
									<h3 className="text-lg font-bold">
										{t(
											`marketing.aiAccessChecker.whyItMatters.${card.key}.title` as any,
										)}
									</h3>
								</div>
								<p className="text-muted-foreground">
									{t(
										`marketing.aiAccessChecker.whyItMatters.${card.key}.description` as any,
									)}
								</p>
							</div>
						</Reveal>
					))}
				</BentoGrid>
			</Section>

			{/* CTA */}
			<Section>
				<Reveal>
					<div className="mx-auto max-w-2xl rounded-2xl border bg-card p-10 text-center">
						<h2 className="text-3xl font-extrabold tracking-tight">
							{t("marketing.aiAccessChecker.cta.title")}
						</h2>
						<p className="mx-auto mt-3 max-w-xl text-muted-foreground">
							{t("marketing.aiAccessChecker.cta.subtitle")}
						</p>
						<CtaPair
							className="mt-6 justify-center"
							primary={t("marketing.aiAccessChecker.cta.button")}
							primaryHref="/auth/signup"
						/>
					</div>
				</Reveal>
			</Section>

			{/* FAQ */}
			<FAQSection
				title={t("marketing.aiAccessChecker.faq.title")}
				description=""
				items={faqItems}
			/>
		</>
	);
}
